import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, EMPTY } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

// Modules à précharger en priorité, même sur les connexions lentes
const PRIORITIZED_MODULES = ['_pages/pages.module'];

// Modules à précharger en second niveau (connexion moyenne ou bonne)
const SECONDARY_MODULES = ['jeunes/jeunes.module', 'proprietaires/proprietaires.module'];

// Modules à précharger uniquement sur connexion excellente
const OPTIONAL_MODULES = ['logements/logements.module', '_forum/forum.module'];

@Injectable({ providedIn: 'root' })
export class NetworkAwarePreloadStrategy implements PreloadingStrategy {
  private connection: any;
  private userHasInteracted = false;

  constructor() {
    // Détection de la connexion réseau de l'utilisateur
    this.connection = this.getConnectionType();
    
    // Détection d'interaction utilisateur pour précharger plus de modules
    if (typeof window !== 'undefined') {
      window.addEventListener('click', () => this.userHasInteracted = true, { once: true });
      window.addEventListener('scroll', () => this.userHasInteracted = true, { once: true });
      window.addEventListener('keydown', () => this.userHasInteracted = true, { once: true });
    }
  }

  // Détecte la qualité de la connexion si l'API est disponible
  private getConnectionType(): any {
    if (typeof navigator !== 'undefined') {
      return (
        navigator.connection ||
        (navigator as any).mozConnection ||
        (navigator as any).webkitConnection
      );
    }
    return null;
  }

  // Détermine si un module doit être préchargé
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    if (!route.path || !route.loadChildren) {
      return EMPTY; // Si pas de chemin défini ou pas de loadChildren, aucun préchargement
    }

    const loadChildrenString = route.loadChildren.toString();

    // Si c'est un module prioritaire, toujours précharger immédiatement
    const isPrioritized = PRIORITIZED_MODULES.some(
      modulePath => loadChildrenString.includes(modulePath)
    );

    if (isPrioritized) {
      return load(); // Préchargement immédiat des modules prioritaires
    }

    // Si c'est un module secondaire, précharger si la connexion est moyenne ou bonne
    const isSecondary = SECONDARY_MODULES.some(
      modulePath => loadChildrenString.includes(modulePath)
    );

    if (isSecondary && this.hasDecentConnection()) {
      // Attendre un court délai pour ne pas bloquer les ressources critiques
      return this.delayPreload(load, 2000);
    }

    // Si c'est un module optionnel, précharger uniquement si excellente connexion
    // ou si l'utilisateur a interagi avec la page
    const isOptional = OPTIONAL_MODULES.some(
      modulePath => loadChildrenString.includes(modulePath)
    );

    if (isOptional && (this.hasExcellentConnection() || this.userHasInteracted)) {
      // Attendre plus longtemps pour les modules optionnels
      return this.delayPreload(load, 4000);
    }

    return EMPTY; // Ne pas précharger les autres modules
  }

  // Préchargement avec délai pour ne pas bloquer les ressources critiques
  private delayPreload(load: () => Observable<any>, delay: number): Observable<any> {
    return of(true).pipe(
      mergeMap(() => new Promise(resolve => setTimeout(resolve, delay))),
      mergeMap(() => load())
    );
  }

  // Vérifie si la connexion est au moins correcte (3G rapide ou mieux)
  private hasDecentConnection(): boolean {
    // Si l'API navigator.connection n'est pas disponible
    if (!this.connection) {
      return true; // Par défaut, considérer comme bonne connexion
    }

    // Vérifier si connexion lente
    if (this.connection.saveData) {
      return false; // L'utilisateur a activé le mode "économie de données"
    }

    // Types de connexion très lente
    const verySlowConnections = ['slow-2g', '2g'];

    // Vérifier le type de connexion
    if (this.connection.effectiveType) {
      return !verySlowConnections.includes(this.connection.effectiveType);
    }

    return true; // Par défaut, précharger
  }

  // Vérifie si la connexion est excellente (4G ou mieux)
  private hasExcellentConnection(): boolean {
    if (!this.connection) {
      return false; // Par défaut, ne pas considérer comme excellente
    }

    if (this.connection.saveData) {
      return false;
    }

    return this.connection.effectiveType === '4g';
  }

  // La méthode originale pour vérifier une bonne connexion
  private hasGoodConnection(): boolean {
    // Si l'API navigator.connection n'est pas disponible
    if (!this.connection) {
      return true; // Par défaut, considérer comme bonne connexion
    }

    // Vérifier si connexion lente
    if (this.connection.saveData) {
      return false; // L'utilisateur a activé le mode "économie de données"
    }

    // Types de connexion lente
    const slowConnections = ['slow-2g', '2g', '3g'];

    // Vérifier le type de connexion
    if (this.connection.effectiveType) {
      return !slowConnections.includes(this.connection.effectiveType);
    }

    return true; // Par défaut, précharger
  }
}
